/**
 * Predictive search web component — loads HTML from the Predictive Search section endpoint.
 * @see https://shopify.dev/docs/api/ajax/reference/predictive-search#get--locale-search-suggest
 */
const DEBOUNCE_MS = 280;
const MIN_QUERY_LEN = 2;

const PREDICTIVE_PARAM_NAMES = [
  'resources[type]',
  'resources[limit]',
  'resources[limit_scope]',
  'resources[options][unavailable_products]',
];

function getShopifyRoot() {
  if (typeof window.Shopify !== 'undefined' && window.Shopify.routes && window.Shopify.routes.root) {
    return window.Shopify.routes.root;
  }
  return '/';
}

function getPredictiveParamsFromForm(form) {
  const params = new URLSearchParams();
  if (!form || !form.elements) return params;
  const set = new Set(PREDICTIVE_PARAM_NAMES);
  for (let i = 0; i < form.elements.length; i++) {
    const el = form.elements[i];
    if (!set.has(el.name)) continue;
    if (el.value !== undefined && el.value !== '') {
      params.append(el.name, el.value);
    }
  }
  return params;
}

function buildSuggestSectionUrl(root, query, sectionId, form) {
  const params = new URLSearchParams();
  params.set('q', query);
  const fromForm = getPredictiveParamsFromForm(form);
  for (const [key, value] of fromForm.entries()) {
    params.append(key, value);
  }
  params.set('section_id', sectionId);
  return `${root}search/suggest?${params.toString()}`;
}

function parseSectionHtml(text, sectionId) {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const sectionEl =
    doc.querySelector(`#shopify-section-${sectionId}`) ||
    doc.querySelector('[id^="shopify-section-"]');
  return sectionEl ? sectionEl.innerHTML.trim() : '';
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this._root = getShopifyRoot();
    this.sectionId = (this.dataset.sectionId || 'predictive-search').trim();
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"], input[name="q"]');
    this.resetBtn = this.querySelector('button[type="reset"]');
    this.resultsPanel = this.querySelector('[data-predictive-results]');
    this.loadingEl = this.querySelector('[data-predictive-loading]');
    this.outlet = this.querySelector('[data-predictive-outlet]');
    this.errorEl = this.querySelector('[data-predictive-error]');
    this.statusEl = this.querySelector('[data-predictive-status]');
    this.emptyStateEl = this.querySelector('[data-predictive-empty]');
    this.resultsSuggestionsEl = this.querySelector('[data-predictive-results-suggestions]');

    this._abort = null;
    this._debounceTimer = null;

    if (this.resultsPanel && this.resultsPanel.id && this.input) {
      this.input.setAttribute('aria-controls', this.resultsPanel.id);
    }

    if (!this.input || !this.form) return;

    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('autocomplete', 'off');

    this._onInput = this._onInput.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onReset = this._onReset.bind(this);

    this.input.addEventListener('input', this._onInput);
    this.input.addEventListener('focus', this._onFocus);
    this.form.addEventListener('submit', this._onSubmit);
    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', this._onReset);
    }

    const initialQ = (this.input.value || '').trim();
    this._showEmptyState(initialQ.length < MIN_QUERY_LEN);
  }

  disconnectedCallback() {
    this._clearDebounce();
    this._abortInFlight();
    if (this.input) {
      this.input.removeEventListener('input', this._onInput);
      this.input.removeEventListener('focus', this._onFocus);
    }
    if (this.form) {
      this.form.removeEventListener('submit', this._onSubmit);
    }
    if (this.resetBtn) {
      this.resetBtn.removeEventListener('click', this._onReset);
    }
  }

  _clearDebounce() {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
  }

  _abortInFlight() {
    if (this._abort) {
      this._abort.abort();
      this._abort = null;
    }
  }

  _setExpanded(open) {
    if (this.input) {
      this.input.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  _showResultsPanel(show) {
    if (!this.resultsPanel) return;
    this.resultsPanel.hidden = !show;
    this._setExpanded(show);
  }

  _setLoading(loading) {
    if (this.loadingEl) {
      this.loadingEl.hidden = !loading;
    }
  }

  _hideError() {
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  _showEmptyState(visible) {
    if (!this.emptyStateEl) return;
    this.emptyStateEl.hidden = !visible;
  }

  _outletHasPredictiveResults() {
    if (!this.outlet) return false;
    return !!this.outlet.querySelector(
      '[data-predictive-search-query-root] ul[role="listbox"] li, [data-predictive-search-root] ul[role="listbox"] li',
    );
  }

  _syncResultsSuggestionsVisibility() {
    if (!this.resultsSuggestionsEl) return;
    this.resultsSuggestionsEl.hidden = !this._outletHasPredictiveResults();
  }

  _onInput() {
    this._clearDebounce();
    const q = (this.input.value || '').trim();

    if (q.length < MIN_QUERY_LEN) {
      this._abortInFlight();
      this._hideError();
      this._setLoading(false);
      this._showResultsPanel(false);
      this._showEmptyState(true);
      if (this.outlet) this.outlet.innerHTML = '';
      if (this.statusEl) this.statusEl.textContent = '';
      if (this.resultsSuggestionsEl) this.resultsSuggestionsEl.hidden = true;
      return;
    }

    this._showEmptyState(false);
    this._debounceTimer = window.setTimeout(() => {
      this._fetchSuggestions(q);
    }, DEBOUNCE_MS);
  }

  _onFocus() {
    const q = (this.input.value || '').trim();
    if (q.length < MIN_QUERY_LEN) {
      this._showEmptyState(true);
      return;
    }
    this._showEmptyState(false);
    if (this.outlet && !this.outlet.innerHTML.trim()) {
      this._fetchSuggestions(q);
      return;
    }
    this._showResultsPanel(true);
  }

  _onSubmit(e) {
    const q = (this.input.value || '').trim();
    if (!q) {
      e.preventDefault();
    }
  }

  _onReset() {
    this._clearDebounce();
    this._abortInFlight();
    this._hideError();
    this._setLoading(false);
    if (this.outlet) this.outlet.innerHTML = '';
    this._showResultsPanel(false);
    this._showEmptyState(true);
    if (this.statusEl) this.statusEl.textContent = '';
    if (this.resultsSuggestionsEl) this.resultsSuggestionsEl.hidden = true;
  }

  async _fetchSuggestions(query) {
    this._abortInFlight();
    const controller = new AbortController();
    this._abort = controller;

    const url = buildSuggestSectionUrl(this._root, query, this.sectionId, this.form);

    this._showResultsPanel(true);
    this._setLoading(true);
    this._hideError();
    if (this.outlet) this.outlet.innerHTML = '';
    if (this.resultsSuggestionsEl) this.resultsSuggestionsEl.hidden = true;

    let res;
    try {
      res = await fetch(url, { signal: controller.signal });
      const text = await res.text();

      if (!res.ok) {
        let message = text;
        try {
          const errJson = JSON.parse(text);
          message = errJson.description || errJson.message || message;
        } catch {
          /* plain text error */
        }
        throw new Error(message || res.statusText || 'Search request failed');
      }

      const inner = parseSectionHtml(text, this.sectionId);
      if (!inner) {
        throw new Error(
          'Could not read predictive search markup. Add sections/predictive-search.liquid and ensure section_id matches the file name.',
        );
      }

      if (this.outlet) {
        this.outlet.innerHTML = inner;
      }

      this._setLoading(false);

      const countEl = this.outlet && this.outlet.querySelector('[data-predictive-result-count]');
      if (this.statusEl && countEl) {
        this.statusEl.textContent = countEl.textContent.trim();
      } else if (this.statusEl) {
        this.statusEl.textContent = '';
      }

      this._syncResultsSuggestionsVisibility();
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._setLoading(false);
      if (this.outlet) this.outlet.innerHTML = '';
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = err.message || 'Search unavailable';
      }
      if (this.statusEl) this.statusEl.textContent = '';
      if (this.resultsSuggestionsEl) this.resultsSuggestionsEl.hidden = true;
    } finally {
      if (this._abort === controller) {
        this._abort = null;
      }
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
